import { axiosInstance } from "./apiClient";
import type { Candidate } from "./preventiviApi";

export interface SearchRequest {
  query: string;
  task?: string;
  top_k?: number;
  context?: Record<string, any>;
  disciplina?: "ELE" | "MEC";
  source_file?: string;
  matching_mode?: "strict" | "browse";
}

export interface CatalogSourceOption {
  source_file: string;
  item_count: number;
}

export interface SearchResponse {
  query: string;
  candidates: Candidate[];
  confidence: number;
  stage5_skipped: boolean;
}

export interface FeedbackRequest {
  query: string;
  item_uid: string;
  signal: 1 | -1;
  replaced_by?: string;
  task?: string;
}

export interface BomItemOut {
  uid: string;
  title: string;
  description: string;
  descrizione_breve?: string | null;
  specs: string;
  codice?: string | null;
  udm?: string | null;
  prezzo?: number | null;
  costo_mat_unitario?: number | null;
  tempo_posa_ore?: number | null;
  costo_mano_unitario?: number | null;
  categoria?: string | null;
  disciplina?: string | null;
  source_file?: string | null;
  anno?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface BomItemUpdate {
  title?: string;
  description?: string;
  descrizione_breve?: string;
  specs?: string;
  codice?: string;
  prezzo?: number | null;
  costo_mat_unitario?: number | null;
  tempo_posa_ore?: number | null;
  costo_mano_unitario?: number | null;
  categoria?: string;
  disciplina?: string;
  anno?: number | null;
}

export interface CatalogListResponse {
  items: BomItemOut[];
  total: number;
  offset: number;
  limit: number;
}

export interface SyncAllResponse {
  synced: number;
}

// The search pipeline (embed + FTS + rerank + LLM Stage 5) can take 15–30s.
const SEARCH_TIMEOUT_MS = 45_000;

export const searcherApi = {
  search: async (req: SearchRequest): Promise<SearchResponse> => {
    const res = await axiosInstance.post(
      "/searcher/search",
      { task: "bom_matching", top_k: 5, ...req },
      { timeout: SEARCH_TIMEOUT_MS },
    );
    return res.data;
  },

  feedback: async (req: FeedbackRequest): Promise<void> => {
    await axiosInstance.post("/searcher/feedback", {
      task: "bom_matching",
      ...req,
    });
  },

  updateBomItem: async (uid: string, changes: BomItemUpdate): Promise<BomItemOut> => {
    const res = await axiosInstance.patch(`/searcher/bom-items/${uid}`, changes);
    return res.data;
  },

  deleteBomItem: async (uid: string): Promise<{ status: string; uid: string }> => {
    const res = await axiosInstance.delete(`/searcher/bom-items/${uid}`);
    return res.data;
  },

  listCatalog: async (params?: {
    q?: string;
    categoria?: string;
    disciplina?: string;
    anno?: number;
    source_file?: string;
    random?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<CatalogListResponse> => {
    const res = await axiosInstance.get("/searcher/catalog", { params });
    return res.data;
  },

  listCatalogSources: async (): Promise<CatalogSourceOption[]> => {
    const res = await axiosInstance.get("/searcher/catalog/sources");
    return res.data;
  },

  syncAll: async (): Promise<SyncAllResponse> => {
    const res = await axiosInstance.post(
      "/searcher/sync-all",
      {},
      { timeout: 300_000 },
    );
    return res.data;
  },
};
